import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Text,
  Caption1,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
} from '@fluentui/react-components';
import {
  DocumentRegular,
  OpenRegular,
  MoreHorizontalRegular,
  LinkRegular,
  DocumentCopyRegular,
} from '@fluentui/react-icons';
import { DrawioFile } from './FileList';
import { getFreshDownloadUrl } from '../services/graphApi';
import { getDrawioOpenFromUrl, getDrawioOneDriveModeUrl, getDrawioSharePointDirectUrl } from '../utils/drawioLinks';

interface FileItemProps {
  file: DrawioFile;
  teamId: string;
  onOpen: () => void;
}

export const FileItem: React.FC<FileItemProps> = ({ file, teamId, onOpen }) => {
  const { t, i18n } = useTranslation();
  const [openingInDrawio, setOpeningInDrawio] = useState(false);

  const handleOpenInDrawioCollaboration = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (file.driveId) {
      window.open(getDrawioSharePointDirectUrl(file.driveId, file.id), '_blank');
    } else {
      window.open(getDrawioOneDriveModeUrl(), '_blank');
    }
  }, [file.driveId, file.id]);

  const handleOpenInDrawioCopy = useCallback(async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      setOpeningInDrawio(true);
      const downloadUrl = await getFreshDownloadUrl(teamId, file.id);
      window.open(getDrawioOpenFromUrl(downloadUrl), '_blank');
    } catch (err: any) {
      console.error('[FileItem] Fehler beim Öffnen in draw.io:', err);
      window.alert(err?.message || t('fileItem.linkError'));
    } finally {
      setOpeningInDrawio(false);
    }
  }, [teamId, file.id]);

  const formatDate = (dateString: string): string => {
    const locale = i18n.language === 'de' ? 'de-DE' : 'en-GB';
    return new Date(dateString).toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleRowClick = (e: React.MouseEvent) => {
    onOpen();
  };

  const handleRowKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <div
      className="file-item"
      role="button"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={handleRowKeyDown}
    >
      <DocumentRegular className="file-icon" fontSize={24} />
      
      <div className="file-info">
        <Text weight="semibold" className="file-name">
          {file.name}
        </Text>
        <div style={{ marginTop: '4px' }}>
          <Caption1 className="file-meta">
            {t('fileItem.created')} {formatDate(file.createdDateTime)} • {t('fileItem.modified')} {formatDate(file.lastModifiedDateTime)} • {formatSize(file.size)}
          </Caption1>
          <Caption1 className="file-meta" style={{ color: '#605E5C', display: 'block', marginTop: '2px' }}>
            {file.path || '—'}
          </Caption1>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        <Button
          appearance="primary"
          icon={<OpenRegular />}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          {t('fileItem.open')}
        </Button>
        <Menu positioning="below-end">
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="secondary"
              icon={<MoreHorizontalRegular />}
              onClick={(e) => e.stopPropagation()}
              aria-label={t('fileItem.moreActions')}
            >
              {t('fileItem.more')}
            </Button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem
                icon={<LinkRegular />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenInDrawioCollaboration();
                }}
              >
                {t('fileItem.openInDrawio')}
              </MenuItem>
              <MenuDivider />
              <MenuItem
                icon={<DocumentCopyRegular />}
                disabled={openingInDrawio}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenInDrawioCopy();
                }}
              >
                {openingInDrawio ? t('fileItem.opening') : t('fileItem.openCopy')}
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
    </div>
  );
};
